/**
 * Iter 67en — Top-6 FAQ accordion preview for the homepage.
 *
 * Self-hides when there are no published FAQs so the section never
 * renders as an empty block. Pulls the most-asked questions (already
 * sorted by popularity_count desc on the backend).
 */
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { BookOpen, ChevronDown, ChevronUp, ArrowRight } from "lucide-react";

export default function HomeFaqPreview() {
  const { i18n } = useTranslation();
  const locale = (i18n.language || "en").split("-")[0];
  const [faqs, setFaqs] = useState(null);
  const [openId, setOpenId] = useState(null);

  useEffect(() => {
    let cancelled = false;
    api.get("/public/faqs", { params: { locale, limit: 6 } })
      .then(({ data }) => { if (!cancelled) setFaqs(data.faqs || []); })
      .catch(() => { if (!cancelled) setFaqs([]); });
    return () => { cancelled = true; };
  }, [locale]);

  if (!faqs || faqs.length === 0) return null;

  /**
   * Iter 67en — FAQPage JSON-LD on the homepage. Replaces the static
   * schema previously emitted by FaqSection so we have ONE
   * popularity-driven FAQPage per page (Google flags duplicates).
   */
  const ldJson = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "inLanguage": locale,
    "mainEntity": faqs.map((f) => ({
      "@type": "Question",
      "name": f.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": (f.answer || "").replace(/\*\*/g, "").trim(),
      },
    })),
  };

  return (
    <section className="py-12 sm:py-16 bg-neutral-50" data-testid="home-faq-preview">
      {/* eslint-disable-next-line react/no-danger */}
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(ldJson) }} />
      <div className="max-w-3xl mx-auto px-4">
        <div className="flex items-end justify-between gap-4 mb-6">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-xl bg-amber-100 text-amber-700 flex items-center justify-center">
              <BookOpen className="h-4 w-4" />
            </div>
            <div>
              <div className="text-[11px] uppercase tracking-wider font-bold text-amber-700">Most asked</div>
              <h2 className="font-display text-2xl sm:text-3xl font-semibold tracking-tight">Quick answers</h2>
            </div>
          </div>
          <Link
            to="/faq"
            className="text-xs font-semibold text-amber-700 hover:text-amber-800 inline-flex items-center gap-1 shrink-0"
            data-testid="home-faq-view-all"
          >
            View all <ArrowRight className="h-3 w-3" />
          </Link>
        </div>

        <ul className="space-y-2" data-testid="home-faq-list">
          {faqs.map((f) => {
            const open = openId === f.id;
            return (
              <li key={f.id} className="rounded-2xl border border-neutral-200 bg-white">
                <button
                  type="button"
                  onClick={() => setOpenId(open ? null : f.id)}
                  className="w-full text-left px-4 py-3 flex items-start gap-3 group"
                  aria-expanded={open}
                  data-testid={`home-faq-row-${f.id}`}
                >
                  <span className="flex-1 font-display text-base font-semibold text-neutral-900 group-hover:text-amber-700 transition">
                    {f.question}
                  </span>
                  {open
                    ? <ChevronUp className="h-4 w-4 text-neutral-400 mt-1 shrink-0" />
                    : <ChevronDown className="h-4 w-4 text-neutral-400 mt-1 shrink-0" />}
                </button>
                {open && (
                  <div className="px-4 pb-4 text-sm text-neutral-700 leading-relaxed whitespace-pre-wrap">
                    {f.answer}
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      </div>
    </section>
  );
}
